import { createContext, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useGetCompany } from '../hooks/useCompany';
import { useLineContext } from '../hooks/useLineContext';
import { useGetUser } from '../hooks/useUser';
import { useAppStore } from '../stores/appStore';
import { useUserStore } from '../stores/userStore';

export const AppContext = createContext(null);

const getRegisterPath = (pathname) => (pathname.includes('/sale') ? '/register/sale' : '/register');
const contextValue = {};

const AppContextProvider = ({ children }) => {
  const navigate = useNavigate();
  const { lineProfile, isLiffReady } = useLineContext();
  const location = useLocation();

  const setAppLoading = useAppStore((state) => state.setAppLoading);
  const setUserInfo = useUserStore((state) => state.setUserInfo);
  const setCompanyInfo = useUserStore((state) => state.setCompanyInfo);

  // -------------------------
  // 1️⃣ Fetch User
  // -------------------------
  const {
    data: userData,
    isError: isErrorUser,
    isFetched: isFetchedUser,
    isLoading: isLoadingUser,
  } = useGetUser({
    enabled: isLiffReady && !!lineProfile?.userId,
    lineuserid: lineProfile?.userId,
  });

  const user = userData?.Data?.data?.[0];
  const hasUserSuccessCode = userData?.Code === '20000';
  const companyId = user?.company_id?._id;

  // -------------------------
  // 2️⃣ Fetch Company
  // -------------------------
  const { data: companyData, isLoading: isLoadingCompany } = useGetCompany({
    enabled: !!companyId,
    id: companyId,
  });

  const company = companyData?.Data?.data?.[0];

  // -------------------------
  // 3️⃣ Sync store
  // -------------------------
  useEffect(() => {
    if (!user) return;
    setUserInfo(user);
  }, [user, setUserInfo]);

  useEffect(() => {
    if (!company) return;
    setCompanyInfo(company);
  }, [company, setCompanyInfo]);

  // -------------------------
  // 4️⃣ Navigation + Loading Logic
  // Wait for LIFF first, then resolve auth with or without a profile.
  // -------------------------
  useEffect(() => {
    if (!isLiffReady) return;

    const registerPath = getRegisterPath(location.pathname);
    const isRegisterPath = location.pathname.startsWith('/register');

    if (!lineProfile?.userId) {
      if (!isRegisterPath) {
        navigate(registerPath, { replace: true });
      }

      setAppLoading(false);
      return;
    }

    // Still fetching — keep loading
    if (isLoadingUser) return;
    if (isLoadingCompany) return;

    // User fetch errored then redirect to register page.
    if (isErrorUser) {
      navigate(registerPath, { replace: true });
      setAppLoading(false);
      return;
    }

    if (isFetchedUser && (!hasUserSuccessCode || !user)) {
      if (!isRegisterPath) {
        navigate(registerPath, { replace: true });
      }

      setAppLoading(false);
      return;
    }

    // User and optional company are ready.
    if (user) {
      if (isRegisterPath) {
        navigate('/home', { replace: true });
      }

      setAppLoading(false);
    }
  }, [
    isLiffReady,
    lineProfile?.userId,
    isLoadingUser,
    isLoadingCompany,
    isErrorUser,
    isFetchedUser,
    hasUserSuccessCode,
    user,
    location.pathname,
    navigate,
    setAppLoading,
  ]);

  return <AppContext.Provider value={contextValue}>{children}</AppContext.Provider>;
};

export default AppContextProvider;
